import { useCallback, useEffect, useState } from "react";
import { Copy, Link2, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { btnGhost, btnPrimary, inputCls } from "@/components/TopBar";

const publicUrl = (path) => `${window.location.origin}${path}`;

export const ShareLinkDialog = ({ trip, vehicle, onClose }) => {
  const [links, setLinks] = useState([]);
  const [days, setDays] = useState(7);
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/shares", { params: { trip_id: trip.id } });
    setLinks(data);
  }, [trip.id]);

  useEffect(() => {
    load().catch(() => toast.error("Could not load share links"));
  }, [load]);

  const copy = async (path) => {
    const url = publicUrl(path);
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
    } catch {
      toast.message(url);
    }
  };

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/shares", {
        trip_id: trip.id,
        label: label.trim() || null,
        expires_days: Number(days) || 7,
      });
      toast.success("Client link created");
      copy(data.path);
      setLabel("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (id) => {
    try {
      await api.delete(`/shares/${id}`);
      toast.success("Link revoked");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="fixed inset-0 z-[1700] flex items-start justify-center overflow-y-auto p-4 py-10" data-testid="share-link-dialog">
      <div className="absolute inset-0 bg-black/80" onClick={onClose} />
      <div className="relative w-full max-w-lg border border-white/15 bg-[#0A0A0A] p-6 rise">
        <button
          onClick={onClose}
          data-testid="close-share-dialog-btn"
          className="absolute right-3 top-3 border border-white/10 p-1.5 text-[#A1A1AA] hover:border-[#00F0FF] hover:text-[#00F0FF]"
          aria-label="Close"
        >
          <X size={14} />
        </button>
        <p className="overline">client access</p>
        <h2 className="font-head text-2xl font-black tracking-tight">SHARE SHIPMENT</h2>
        <p className="mt-2 text-[11px] text-[#A1A1AA]">
          {vehicle.plate_number} · {trip.origin_name} → {trip.destination_name}. Anyone with the link can
          watch this one shipment - no login, no other trucks, no driver contact details.
        </p>

        <div className="mt-6 grid gap-4 sm:grid-cols-3">
          <div className="sm:col-span-2">
            <label className="overline mb-2 block">label (optional)</label>
            <input
              data-testid="share-label-input"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Acme Steel - PO 4471"
              className={inputCls}
            />
          </div>
          <div>
            <label className="overline mb-2 block">expires in (days)</label>
            <input
              data-testid="share-days-input"
              type="number"
              min="1"
              max="90"
              value={days}
              onChange={(e) => setDays(e.target.value)}
              className={inputCls}
            />
          </div>
        </div>

        <button onClick={create} disabled={busy} data-testid="create-share-btn" className={`${btnPrimary} mt-5 w-full py-3`}>
          <Link2 size={12} /> {busy ? "creating" : "create client link"}
        </button>

        <p className="overline mt-6 mb-2">existing links</p>
        <div className="doot-scroll max-h-52 overflow-y-auto border border-white/10" data-testid="share-link-list">
          {links.length === 0 && (
            <p className="px-3 py-5 text-xs text-[#52525B]" data-testid="share-links-empty">
              NO LINKS YET
            </p>
          )}
          {links.map((l) => (
            <div
              key={l.id}
              data-testid={`share-link-row-${l.id}`}
              className="flex items-center gap-2 border-b border-white/5 px-3 py-3"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-[11px]" style={{ color: l.revoked ? "#52525B" : "#00F0FF" }}>
                  {publicUrl(l.path)}
                </p>
                <p className="overline mt-1">
                  {l.revoked ? "revoked" : `${l.views} views`} · {l.label || "no label"}
                </p>
              </div>
              {!l.revoked && (
                <>
                  <button
                    onClick={() => copy(l.path)}
                    data-testid={`copy-share-btn-${l.id}`}
                    className={btnGhost}
                    aria-label="Copy link"
                  >
                    <Copy size={11} />
                  </button>
                  <button
                    onClick={() => revoke(l.id)}
                    data-testid={`revoke-share-btn-${l.id}`}
                    className="border border-white/10 p-1.5 text-[#A1A1AA] hover:border-[#FF3B30] hover:text-[#FF3B30]"
                    aria-label="Revoke link"
                  >
                    <Trash2 size={11} />
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
